/* eslint-disable react-hooks/exhaustive-deps */
import { Add } from "@mui/icons-material";
import { Autocomplete, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormGroup, Grid, InputLabel, OutlinedInput, styled, Tab, Tabs, TextField, Typography } from "@mui/material";
import { ClockIcon } from "@mui/x-date-pickers";
import { useEffect, useState } from "react";
import Overview from "./Overview";
import PointInTime from "./PointInTime";
import { BudgetType } from "../../../../common/const";
import { LoadingPage } from "../../../../common/LoadingPage";
import { showNotification } from "../../../../common/Notification/ToastifyNotification";
import { useSelector } from "react-redux";
import { apiRequest } from "../../../../common/common";
import moment from "moment";

const DemoTabs = styled(Tabs)(({ theme }) => ({
    minWidth: 200,
    [theme.breakpoints.down('xs')]: {
        marginLeft: '0%',
    },
    [theme.breakpoints.down('md')]: {
        minWidth: '20px',
        fontSize: 10,
    },
}));

const DemoTab = styled(Tab)(({ theme }) => ({
    minWidth: 110,
    fontSize: 14,
    fontFamily: 'open sans',
    fontWeight: 'bold',
    [theme.breakpoints.down('xs')]: {
        minWidth: 8,
        fontSize: 10,
    },
    '&:hover': {
        backgroundColor: '#f6f7ff',
        color: 'black',
    },
}));

const BudgetModule = ({
    projectId,
    projectData,
    getData,
    budget,
    totalTax,
    tax
}) => {
    const { user, roles } = useSelector((state) => state.users);
    const isUser = roles.find((a) => a.id === user?.role_id);
    const [activeTab, setActiveTab] = useState(0);
    const [budgetOpen, setBudgetOpen] = useState(false);
    const [defaultBudget, setDefaultBudget] = useState(false);
    const [budgetName, setBudgetName] = useState(null);
    const [budgetAmount, setBudgetAmount] = useState(null);
    const [budgetID, setBudgetID] = useState(null);
    const [expenseCode, setExpenseCode] = useState(null);
    const [budgetType, setBudgetType] = useState(BudgetType[0]?.value);
    const [dataLoading, setDataLoading] = useState(false);
    const [budgetData, setBudgetData] = useState([]);
    const [pointName, setPointName] = useState(`Point In Time ${moment().format('YYYY-MM-DD hh:mm A')}`);
    const [pointOpen, setPointOpen] = useState(false);
    const [defaultPoint, setDefaultPoint] = useState(false);
    const [pointNote, setPointNote] = useState(null);
    const [createBudget, setCreateBudget] = useState(false);
    const [budgetId, setBudgetId] = useState(null);
    const [pointData, setPointData] = useState(null);

    const handleClose = () => {
        setBudgetOpen(false);
        setBudgetAmount(null);
        setBudgetName(null);
        setDefaultBudget(false);
        setBudgetID(null);
        setExpenseCode(null);
        setCreateBudget(false);
        setBudgetType(BudgetType[0]?.value);
    }

    const getBudget = async () => {
        try {
            setDataLoading(true);
            const response = await apiRequest('get', `/budget?projectId=${projectId}&pageNo=1&pageSize=10`);
            const { data } = response.data;
            if (data?.length > 0) {
                setBudgetData(data);
            }
            setDataLoading(false);
        } catch (error) {
            setDataLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    const getPointInTime = async () => {
        try {
            setDataLoading(true);
            const response = await apiRequest('get', `/point-in-time?projectId=${projectId}`);
            if (response.data) {
                setPointData(response?.data?.data);
                setDataLoading(false);
            }
        } catch (error) {
            setDataLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    useEffect(() => {
        if (projectId) {
            getPointInTime();
        }
    }, [projectId]);

    const handleSubmit = async () => {
        if (budgetName) {
            if (budgetAmount) {
                try {
                    setDataLoading(true);
                    const data = {
                        is_default: defaultBudget,
                        approved_amount: Number(budgetAmount),
                        budget_name: budgetName,
                        expanse_code: expenseCode,
                        type: budgetType,
                        budget_no: budgetID,
                        status: 'active',
                        project_id: projectId,
                        created_by: user?.user_id,
                        organization_id: user?.organization?.organization_id
                    }
                    let response;
                    if (budgetId) {
                        response = await apiRequest('put', `/budget/${budgetId}`, data);
                    } else {
                        response = await apiRequest('post', '/budget', data);
                    }
                    if (response.data) {
                        handleClose();
                        getBudget();
                        showNotification(response?.data?.message, 'success');
                    }
                    setDataLoading(false);
                } catch (error) {
                    setDataLoading(false);
                    showNotification(error?.response?.data.error || error.message, 'error');
                }
            } else {
                showNotification('Budget Amount is required', 'error');
            }
        } else {
            showNotification('Budget Name is required', 'error');
        }
    }

    useEffect(() => {
        if (projectId) {
            getBudget();
        }
    }, [projectId]);

    const getInitialBudget = async () => {
        try {
            setDataLoading(true);
            const response = await apiRequest('get', `/budget/${budgetId}`);
            const { data } = response.data;
            setDefaultBudget(data?.is_default);
            setBudgetName(data?.budget_name);
            setBudgetAmount(data?.approved_amount);
            setBudgetID(data?.budget_no);
            setBudgetType(data?.type);
            setExpenseCode(data?.expanse_code);
            setDataLoading(false);
        } catch (error) {
            setDataLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    useEffect(() => {
        if (budgetId !== null && budgetOpen && !createBudget) {
            getInitialBudget();
        }
    }, [budgetId, budgetOpen]);

    return (
        <>
            {dataLoading && (<LoadingPage isLoading={dataLoading} />)}
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Budget</Typography>
                {isUser?.code !== 'orgGuestUser' && (
                    <Box display="flex" gap={1}>
                        <Button
                            size="Medium"
                            variant="outlined"
                            color="primary"
                            startIcon={<ClockIcon />}
                            style={{
                                textTransform: 'capitalize'
                            }}
                            onClick={() => setPointOpen(true)}
                        >
                            Create Point In Time
                        </Button>
                        <Button
                            size="Medium"
                            variant="contained"
                            color="primary"
                            startIcon={<Add />}
                            style={{
                                textTransform: 'capitalize'
                            }}
                            onClick={() => {
                                setBudgetOpen(true);
                                setCreateBudget(true);
                            }}
                        >
                            Create Budget
                        </Button>
                    </Box>
                )}
            </Box>
            <Dialog
                open={pointOpen}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Create Point In Time</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 14 }}>A Point in Time is a snapshot of the  financial health of the project at a specific date and time. Once created, it can be used to be compare financial data in history tables, reports, and item detail pages.</Typography>
                    <Typography variant="body1" style={{ fontWeight: 'bold' }} mt={1}>Details</Typography>
                    <Grid container spacing={2}>
                        <Grid item sm={12}>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={defaultPoint}
                                            onClick={(e) => {
                                                setDefaultPoint(e.target.checked)
                                            }}
                                        />}
                                    label="Set this as the default Point In Time for this project"
                                />
                            </FormGroup>
                        </Grid>
                        <Grid item sm={12}>
                            <FormControl size="small" fullWidth variant="outlined">
                                <InputLabel htmlFor="standard-adornment-password">Name *</InputLabel>
                                <OutlinedInput
                                    label="Name *"
                                    id="standard-adornment-password"
                                    type='text'
                                    value={pointName}
                                    onChange={(e) => {
                                        setPointName(e.target.value)
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item sm={12}>
                            <FormControl size="small" fullWidth variant="outlined">
                                <InputLabel htmlFor="standard-adornment-password">Note</InputLabel>
                                <OutlinedInput
                                    label="Note"
                                    id="standard-adornment-password"
                                    type='text'
                                    multiline
                                    minRows={3}
                                    maxRows={3}
                                    value={pointNote}
                                    onChange={(e) => {
                                        setPointNote(e.target.value)
                                    }}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        size="small"
                        color="secondary"
                        variant="outlined"
                        onClick={() => {
                            setPointOpen(false);
                            setDefaultPoint(false);
                            setPointNote(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        onClick={async () => {
                            try {
                                setDataLoading(true);
                                const data = {
                                    "name": pointName,
                                    "note": pointNote,
                                    "type": "manual",
                                    "status": "active",
                                    "project_id": projectId,
                                    "created_by": user?.user_id,
                                    "organization_id": user?.organization?.organization_id,
                                    "is_default": defaultPoint
                                }
                                const response = await apiRequest('post', '/point-in-time', data);
                                if (response.data) {
                                    setPointOpen(false);
                                    setDefaultPoint(false);
                                    setPointNote(null);
                                    getPointInTime();
                                    showNotification(response?.data?.message, 'success');
                                }
                                setDataLoading(false);
                            } catch (error) {
                                setDataLoading(false);
                                showNotification(error?.response?.data.error || error.message, 'error');
                            }
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={budgetOpen}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Create Custom Budget</DialogTitle>
                <DialogContent>
                    {dataLoading && (<LoadingPage isLoading={dataLoading} />)}
                    <Grid container spacing={2}>
                        <Grid item sm={12}>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={defaultBudget}
                                            onClick={(e) => {
                                                setDefaultBudget(e.target.checked)
                                            }}
                                        />}
                                    label="Set this as the default Budget for this project"
                                />
                            </FormGroup>
                        </Grid>
                        <Grid item sm={12}>
                            <FormControl size="small" fullWidth variant="outlined">
                                <InputLabel htmlFor="standard-adornment-password">Name *</InputLabel>
                                <OutlinedInput
                                    label="Name *"
                                    id="standard-adornment-password"
                                    type='text'
                                    value={budgetName || ''}
                                    onChange={(e) => {
                                        setBudgetName(e.target.value)
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item sm={12} md={6}>
                            <FormControl size="small" fullWidth variant="outlined">
                                <InputLabel htmlFor="standard-adornment-password">Budget Amount *</InputLabel>
                                <OutlinedInput
                                    label="Budget Amount *"
                                    id="standard-adornment-password"
                                    type='number'
                                    value={budgetAmount || ''}
                                    onChange={(e) => {
                                        setBudgetAmount(e.target.value)
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item sm={12} md={6}>
                            <FormControl size="small" fullWidth variant="outlined">
                                <InputLabel htmlFor="standard-adornment-password">Budget ID</InputLabel>
                                <OutlinedInput
                                    label="Budget ID"
                                    id="standard-adornment-password"
                                    type='text'
                                    value={budgetID || ''}
                                    onChange={(e) => {
                                        setBudgetID(e.target.value)
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item sm={12} md={6}>
                            <Autocomplete
                                value={BudgetType.find((c) => c.value === budgetType) || null}
                                id="combo-box-demo"
                                size="small"
                                fullWidth
                                clearIcon={null}
                                options={BudgetType}
                                getOptionLabel={(option) => option?.name}
                                onChange={(event, newValue) => {
                                    setBudgetType(newValue.value || null);
                                }}
                                renderInput={(params) => <TextField fullWidth {...params} label="Budget Type *" name="Budget Type *" variant="outlined" />}
                            />
                        </Grid>
                        <Grid item sm={12} md={6}>
                            <FormControl size="small" fullWidth variant="outlined">
                                <InputLabel htmlFor="standard-adornment-password">Expense Code</InputLabel>
                                <OutlinedInput
                                    label="Expense Code"
                                    id="standard-adornment-password"
                                    type='text'
                                    value={expenseCode || ''}
                                    onChange={(e) => {
                                        setExpenseCode(e.target.value)
                                    }}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        size="small"
                        color="secondary"
                        variant="outlined"
                        onClick={() => handleClose()}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        onClick={() => handleSubmit()}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', margin: '-12px 0px 0px 0px' }}>
                <DemoTabs
                    value={activeTab}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <DemoTab label="Overview" value={0} onClick={() => setActiveTab(0)} />
                    <DemoTab label="Points In Time" value={1} onClick={() => setActiveTab(1)} />
                </DemoTabs>
            </Box>
            {activeTab === 0 && (
                <Overview
                    projectId={projectId}
                    budgetData={budgetData}
                    setBudgetOpen={setBudgetOpen}
                    budgetId={budgetId}
                    setBudgetId={setBudgetId}
                    getBudget={getBudget}
                    projectData={projectData}
                    getData={getData}
                    budget={budget}
                    totalTax={totalTax}
                    tax={tax}
                    pointData={pointData}
                />
            )}
            {activeTab === 1 && (
                <PointInTime
                    projectId={projectId}
                    getPointInTime={getPointInTime}
                    pointData={pointData}
                />
            )}
        </>
    )
}

export default BudgetModule;